// SPDX-License-Identifier: AGPL-3.0-or-later
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Dataset, Channel } from '../types';
import { num, rate, esc, CHANNEL_COLOUR, CHANNEL_LABEL } from '../format';
import { gloss } from '../glossary';
import { openRegion } from '../main';

type Mode = 'points' | 'access';
const CHANNEL_ORDER: Channel[] = ['branch', 'bankpost', 'other', 'atm'];

export function renderMap(root: HTMLElement, data: Dataset): void {
  const { points, regions, meta } = data;
  let mode: Mode = 'points';
  const shown: Record<Channel, boolean> = { branch: true, bankpost: true, other: true, atm: false };

  root.innerHTML = `
    <div class="view-head">
      <h2>The map of physical banking</h2>
      <p>Every staffed counter and bank ATM in ${meta.latestYear}, on the spot APRA reported it. Switch to <strong>Access</strong> to shade each ${gloss('sa3', 'region')} by ${gloss('perCapita', 'branches per 10,000 residents')} — the deep-blue city cores against the pale, thinly-served bush.</p>
    </div>
    <div class="controls">
      <div class="seg" role="tablist" aria-label="Map mode">
        <button data-mode="points" aria-pressed="true">Service points</button>
        <button data-mode="access" aria-pressed="false">Access per capita</button>
      </div>
      <div class="seg" id="chan-toggles" aria-label="Channels">
        ${CHANNEL_ORDER.map((ch) => `<button data-chan="${ch}" aria-pressed="${shown[ch]}"><span class="legend-dot" style="background:${CHANNEL_COLOUR[ch]};display:inline-block;margin-right:5px;vertical-align:-1px"></span>${CHANNEL_LABEL[ch]}</button>`).join('')}
      </div>
    </div>
    <div class="panel">
      <div class="map-shell"><div class="map-canvas" id="map"></div></div>
      <div class="map-legend" id="map-legend"></div>
    </div>
  `;

  const canvas = root.querySelector('#map') as HTMLElement;
  const map = L.map(canvas, { minZoom: 3, maxZoom: 14, zoomControl: true, scrollWheelZoom: false, preferCanvas: true });
  map.attributionControl.setPrefix(false);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
    attribution: 'Tiles © CARTO · Data: APRA points of presence · Boundaries: ABS ASGS (CC BY 4.0)',
    subdomains: 'abcd', minZoom: 3, maxZoom: 14,
  }).addTo(map);
  map.setView([-28.3, 134], 4);

  const renderer = L.canvas({ padding: 0.5 });
  const layerByChannel: Record<Channel, L.LayerGroup> = {
    branch: L.layerGroup(), bankpost: L.layerGroup(), other: L.layerGroup(), atm: L.layerGroup(),
  };
  // build point markers once
  for (let i = 0; i < points.lat.length; i++) {
    const ch = points.channels[points.ch[i]];
    const lat = points.lat[i], lon = points.lon[i];
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    const inst = points.insts[points.inst[i]];
    const marker = L.circleMarker([lat, lon], {
      renderer, radius: ch === 'branch' ? 4 : 3, weight: 0.6, color: '#ffffff',
      fillColor: CHANNEL_COLOUR[ch], fillOpacity: 0.82,
    });
    marker.bindTooltip(`${esc(inst)}<br>${CHANNEL_LABEL[ch]} · ${esc(points.sub[i])}, ${esc(points.st[i])}`, { direction: 'top', opacity: 0.95, className: 'map-tip' });
    const code = points.code[i];
    if (code) marker.on('click', () => openRegion(code));
    layerByChannel[ch].addLayer(marker);
  }

  // choropleth layer (lazy)
  let choropleth: L.GeoJSON | null = null;
  const regByCode = new Map(regions.map((r) => [r.code, r]));
  const served = regions.filter((r) => r.perCapita != null && r.pop && r.pop > 3000).map((r) => r.perCapita!) as number[];
  served.sort((a, b) => a - b);
  const median = served.length ? served[Math.floor(served.length / 2)] : 1;
  const thresholds = [0.35, 0.7, 1.15, 2].map((m) => +(m * median).toFixed(3));
  const rampFor = (v: number | null): string => {
    if (v == null) return 'var(--no-data)';
    const ramp = ['#eef2f5', '#cfe0e2', '#93c1bf', '#4f8f9c', '#22566a'];
    let i = 0; while (i < thresholds.length && v >= thresholds[i]) i++;
    return ramp[i];
  };

  async function ensureChoropleth(): Promise<L.GeoJSON> {
    if (choropleth) return choropleth;
    const geo = await fetch('data/sa3.geojson').then((r) => { if (!r.ok) throw new Error('boundaries failed'); return r.json(); });
    choropleth = L.geoJSON(geo, {
      style: (f: any) => {
        const r = regByCode.get(String(f.properties.sa3_code_2021));
        return { fillColor: rampFor(r?.perCapita ?? null), fillOpacity: 0.82, color: '#ffffff', weight: 0.5 };
      },
      onEachFeature: (f: any, lyr: any) => {
        const r = regByCode.get(String(f.properties.sa3_code_2021));
        const name = f.properties.sa3_name_2021;
        lyr.bindTooltip(
          `<strong>${esc(name)}</strong><br>${r ? `${rate(r.perCapita, 2)} branches / 10k · ${num(r.branchNow)} branches` : 'no data'}`,
          { sticky: true, className: 'map-tip' },
        );
        lyr.on({
          mouseover: () => lyr.setStyle({ weight: 2, color: '#14202e' }),
          mouseout: () => choropleth!.resetStyle(lyr),
          click: () => { if (r) openRegion(r.code); },
        });
      },
    });
    return choropleth;
  }

  function syncPointLayers(): void {
    for (const ch of CHANNEL_ORDER) {
      if (mode === 'points' && shown[ch]) { if (!map.hasLayer(layerByChannel[ch])) layerByChannel[ch].addTo(map); }
      else map.removeLayer(layerByChannel[ch]);
    }
  }

  function legend(): void {
    const el = root.querySelector('#map-legend') as HTMLElement;
    if (mode === 'points') {
      el.innerHTML = CHANNEL_ORDER.filter((ch) => shown[ch])
        .map((ch) => `<span class="legend-item"><span class="legend-dot" style="background:${CHANNEL_COLOUR[ch]}"></span>${CHANNEL_LABEL[ch]}</span>`).join('')
        + `<span class="legend-item" style="color:var(--text-tertiary)">Click a point to open its region</span>`;
    } else {
      const ramp = ['#eef2f5', '#cfe0e2', '#93c1bf', '#4f8f9c', '#22566a'];
      el.innerHTML = `<span class="legend-item">Fewer branches / person</span><span class="ramp">${ramp.map((c) => `<span style="background:${c}"></span>`).join('')}</span><span class="legend-item">More</span>`
        + `<span class="legend-item" style="color:var(--text-tertiary)">Thresholds: ${thresholds.map((t) => t.toFixed(2)).join(', ')} per 10k · grey = no data</span>`;
    }
  }

  async function apply(): Promise<void> {
    if (mode === 'access') {
      syncPointLayers();
      const layer = await ensureChoropleth();
      if (!map.hasLayer(layer)) layer.addTo(map);
    } else {
      if (choropleth && map.hasLayer(choropleth)) map.removeLayer(choropleth);
      syncPointLayers();
    }
    legend();
  }

  root.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((b) =>
    b.addEventListener('click', () => {
      mode = b.dataset.mode as Mode;
      root.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      const toggles = root.querySelector('#chan-toggles') as HTMLElement;
      toggles.style.opacity = mode === 'points' ? '1' : '.4';
      toggles.style.pointerEvents = mode === 'points' ? 'auto' : 'none';
      void apply();
    }),
  );
  root.querySelectorAll<HTMLButtonElement>('[data-chan]').forEach((b) =>
    b.addEventListener('click', () => {
      const ch = b.dataset.chan as Channel;
      shown[ch] = !shown[ch];
      b.setAttribute('aria-pressed', String(shown[ch]));
      void apply();
    }),
  );

  void apply();

  // zero-size defence
  const fit = () => { map.invalidateSize(); };
  const ro = new ResizeObserver(() => { if (canvas.clientHeight > 50) { fit(); ro.disconnect(); } });
  ro.observe(canvas);
  setTimeout(fit, 400);
}
